// frontend/src/pages/ReportsApi/index.js
// Relatório de uso da API pública (/reports/api). Sem mockup dedicado: segue a
// estrutura visual do /reports (filtros → KPIs → gráfico → quebras → tabela).
// Dados: GET /api-usage/report/summary e /calls (ApiRequestLogs).
import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles, alpha, useTheme } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Select from "@material-ui/core/Select";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import EventIcon from "@material-ui/icons/Event";
import SearchIcon from "@material-ui/icons/Search";
import GetAppIcon from "@material-ui/icons/GetApp";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import SwapVertIcon from "@material-ui/icons/SwapVert";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import BlockIcon from "@material-ui/icons/Block";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import TimerIcon from "@material-ui/icons/Timer";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip, Legend, CartesianGrid } from "recharts";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import ReportsNav from "../../components/ReportsNav";
import useAerosSettingsStyles from "../../components/Settings/aerosSettingsStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import { PRESETS, presetRange, detectPreset, periodLabel, updatedAgo } from "../Reports/reportRules";
import exportApiXlsx from "./exportXlsx";
import {
  DEFAULT_FILTERS, hasActiveFilters, paramsOf, OUTCOME_PILLS, outcomeMeta, KINDS, ROUTES, CHANNELS,
  kindLabel, channelLabel, originLabel, formatMs, successRate, chartRows, whenLabel, pageRange,
} from "./apiReportRules";

const GRID = "120px 1.1fr 1fr 1fr 0.8fr 1fr 60px 1.1fr 80px 1.6fr 72px";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)";
  const line = isLight ? theme.palette.divider : "rgba(255,255,255,0.06)";
  const purple = stateText(theme, "#C08BFF", "purple");
  const tone = (hex, key) => ({ color: stateText(theme, hex, key), backgroundColor: alpha(hex, 0.1) });
  return {
    pageHeader: { minHeight: 60, flexShrink: 0, display: "flex", alignItems: "center", gap: 12, padding: "0 12px", borderBottom: `1px solid ${line}`, flexWrap: "wrap" },
    pageTitle: { fontFamily: AEROS.fontDisplay, fontSize: 16, fontWeight: 700, letterSpacing: "-0.02em", color: theme.palette.text.primary, "& span": { color: theme.palette.text.secondary, fontWeight: 500 } },
    pageSubtitle: { fontSize: 12, color: theme.palette.text.secondary },
    actions: { marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" },
    pageBody: { flex: 1, minHeight: 0, padding: "16px 12px 24px", overflowY: "auto", display: "flex", flexDirection: "column", gap: 14, "& > *": { flexShrink: 0 }, ...theme.scrollbarStyles },
    card: { backgroundColor: theme.palette.aeros.surface, border: `1px solid ${line}`, borderRadius: 14, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12, minWidth: 0 },
    cardTitle: { fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: theme.palette.aeros.textTertiary },
    row: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
    segmented: { display: "flex", border: `1px solid ${border}`, borderRadius: 10, overflow: "hidden", fontSize: 12, fontWeight: 600, flex: "none" },
    seg: { padding: "10px 12px", minHeight: 44, color: theme.palette.text.secondary, background: "none", border: "none", cursor: "pointer", font: "inherit" },
    segOn: { backgroundColor: alpha("#A044FF", 0.2), color: purple },
    pill: { display: "flex", alignItems: "center", gap: 6, minHeight: 44, backgroundColor: theme.palette.background.paper, border: `1px solid ${border}`, borderRadius: 10, padding: "6px 12px", fontSize: 12.5, color: theme.palette.text.secondary, whiteSpace: "nowrap" },
    pillOn: { borderColor: alpha(AEROS.cyan, 0.35) },
    pillIcon: { fontSize: 16 },
    pillSelect: { fontSize: 12.5, color: theme.palette.text.primary, fontWeight: 600, "& .MuiNativeSelect-select": { padding: 0, paddingRight: 20, fontSize: 16, background: "transparent", "&:focus": { background: "transparent" }, [theme.breakpoints.up("sm")]: { fontSize: 12.5 } }, "& .MuiNativeSelect-icon": { fontSize: 18, color: theme.palette.aeros.textTertiary, top: "calc(50% - 9px)" } },
    dateField: { background: "transparent", border: "none", outline: "none", padding: 0, font: "inherit", fontSize: 16, color: theme.palette.text.primary, colorScheme: theme.palette.type, [theme.breakpoints.up("sm")]: { fontSize: 12.5 } },
    clear: { marginLeft: "auto", minHeight: 44, fontSize: 12, fontWeight: 600, color: stateText(theme, AEROS.cyan, "info"), background: "none", border: "none", cursor: "pointer", font: "inherit" },
    search: { flex: 1, display: "flex", gap: 8, alignItems: "center", minHeight: 44, backgroundColor: theme.palette.background.paper, border: `1px solid ${border}`, borderRadius: 10, padding: "6px 12px", fontSize: 13, color: theme.palette.text.secondary, minWidth: 200, "& input": { flex: 1, minWidth: 0, background: "none", border: "none", outline: "none", font: "inherit", fontSize: 16, color: theme.palette.text.primary, [theme.breakpoints.up("sm")]: { fontSize: 13 } } },
    statusPills: { display: "flex", gap: 6, flexWrap: "wrap" },
    sPill: { borderRadius: 99, minHeight: 44, padding: "5px 12px", fontSize: 11.5, fontWeight: 600, color: theme.palette.text.secondary, border: `1px solid ${border}`, background: "none", cursor: "pointer", font: "inherit", display: "flex", gap: 5, alignItems: "center" },
    sPillOn: { color: purple, backgroundColor: alpha("#A044FF", 0.15), borderColor: "transparent" },
    kpis: { display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: 14, [theme.breakpoints.down("md")]: { gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }, [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr 1fr" } },
    kpi: { backgroundColor: theme.palette.aeros.surface, border: `1px solid ${line}`, borderRadius: 14, padding: "16px 18px", minWidth: 0 },
    kpiHead: { display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: theme.palette.text.secondary, "& .MuiSvgIcon-root": { fontSize: 17 } },
    kpiValue: { fontSize: 24, fontWeight: 700, letterSpacing: "-0.03em", marginTop: 8, fontVariantNumeric: "tabular-nums", color: theme.palette.text.primary },
    kpiHint: { fontSize: 11.5, marginTop: 3, color: theme.palette.text.secondary, minHeight: 15 },
    chartBox: { height: 220, width: "100%", minWidth: 0 },
    breakdowns: { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 14, [theme.breakpoints.down("md")]: { gridTemplateColumns: "1fr 1fr" }, [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" } },
    bRow: { display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, minHeight: 28, "& b": { marginLeft: "auto", fontVariantNumeric: "tabular-nums" } },
    ell: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 },
    tableWrap: { overflowX: "auto", "& > div": { minWidth: 1180 } },
    tHead: { display: "grid", gridTemplateColumns: GRID, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: theme.palette.aeros.textTertiary, borderBottom: `1px solid ${line}`, "& > div": { padding: "12px 8px" }, "& > div:first-child": { paddingLeft: 20 }, "& > div:last-child": { paddingRight: 20, textAlign: "right" } },
    tRow: { display: "grid", gridTemplateColumns: GRID, alignItems: "center", borderBottom: `1px solid ${line}`, fontSize: 13, fontVariantNumeric: "tabular-nums", color: theme.palette.text.primary, "&:hover": { backgroundColor: alpha("#A044FF", 0.04) }, "& > div": { padding: "10px 8px", minWidth: 0 }, "& > div:first-child": { paddingLeft: 20 }, "& > div:last-child": { paddingRight: 20 } },
    secondary: { fontSize: 12.5, color: theme.palette.text.secondary },
    status: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, borderRadius: 99, padding: "3px 10px", whiteSpace: "nowrap", "& i": { width: 6, height: 6, borderRadius: "50%", background: "currentColor" } },
    info: tone(AEROS.cyan, "info"), success: tone(AEROS.success, "success"), warning: tone(AEROS.warning, "warning"), error: tone(AEROS.error, "error"), purple: tone("#C08BFF", "purple"),
    muted: { color: theme.palette.text.secondary, backgroundColor: isLight ? theme.palette.action.hover : "rgba(255,255,255,0.06)" },
    errCell: { fontSize: 12, color: stateText(theme, AEROS.error, "error"), overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    rowActions: { display: "flex", justifyContent: "flex-end" },
    actBtn: { width: 44, height: 44, borderRadius: 8, color: theme.palette.text.secondary, "& .MuiSvgIcon-root": { fontSize: 16 } },
    foot: { display: "flex", alignItems: "center", padding: "12px 20px", fontSize: 12, color: theme.palette.text.secondary, gap: 10, flexWrap: "wrap" },
    pages: { marginLeft: "auto", display: "flex", gap: 4 },
    pg: { width: 44, height: 44, borderRadius: 8, border: `1px solid ${border}`, display: "flex", alignItems: "center", justifyContent: "center", background: "none", cursor: "pointer", font: "inherit", fontSize: 12, color: theme.palette.text.primary, "&:disabled": { opacity: 0.4, cursor: "default" } },
    pgOn: { backgroundColor: alpha("#A044FF", 0.2), color: purple, fontWeight: 700, borderColor: "transparent" },
    empty: { padding: "40px 20px", textAlign: "center", fontSize: 13, color: theme.palette.text.secondary },
  };
});

const t = (k, o) => i18n.t(`aeros.apiReport.${k}`, o);

const SERIES = [
  { key: "success", color: AEROS.success },
  { key: "queued", color: AEROS.cyan },
  { key: "rejected", color: AEROS.warning },
  { key: "error", color: AEROS.error },
  { key: "failed", color: "#C08BFF" },
];

// Tooltip do gráfico de barras empilhadas: uma linha por série + total do
// dia, para não obrigar quem lê a somar as fatias mentalmente.
const ApiChartTooltip = ({ active, payload, label, theme }) => {
  if (!active || !payload || !payload.length) return null;
  const total = payload.reduce((sum, p) => sum + (Number(p.value) || 0), 0);
  return (
    <div style={{ background: theme.palette.aeros.surface, border: "none", borderRadius: 10, fontSize: 12, padding: "8px 12px", minWidth: 120 }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
          <span>{t(`pills.${p.dataKey}`)}</span>
          <span>{p.value}</span>
        </div>
      ))}
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginTop: 4, fontWeight: 700 }}>
        <span>{t("kpis.total")}</span>
        <span>{total}</span>
      </div>
    </div>
  );
};

const ReportsApi = () => {
  const classes = useStyles();
  const aeros = useAerosSettingsStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  void user;

  const [filters, setFilters] = useState(() => DEFAULT_FILTERS());
  const [applied, setApplied] = useState(() => DEFAULT_FILTERS());
  const [whatsapps, setWhatsapps] = useState([]);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    api.get("/whatsapp").then(({ data }) => setWhatsapps(data || [])).catch(() => {});
  }, []);

  const load = useCallback(async (f, p, size) => {
    setLoading(true);
    try {
      const [calls, sum] = await Promise.all([
        api.get("/api-usage/report/calls", { params: { ...paramsOf(f), page: p, pageSize: size } }),
        api.get("/api-usage/report/summary", { params: paramsOf(f) }).then((r) => r.data).catch(() => null),
      ]);
      setRows(calls.data.rows || []);
      setTotal(Number(calls.data.total || 0));
      setSummary(sum);
      setApplied(f);
      setPage(p);
      setUpdatedAt(new Date());
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(filters, 1, pageSize); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = (f = filters, p = 1, size = pageSize) => { setFilters(f); load(f, p, size); };
  const set = (patch) => setFilters((prev) => ({ ...prev, ...patch }));
  const preset = detectPreset(filters.dateFrom, filters.dateTo);
  const counters = summary?.counters || null;

  const kpis = useMemo(() => [
    { key: "total", label: t("kpis.total"), value: counters?.total ?? total, Icon: SwapVertIcon, accent: AEROS.cyan },
    { key: "successRate", label: t("kpis.successRate"), value: successRate(counters), Icon: CheckCircleOutlineIcon, accent: AEROS.success },
    { key: "rejected", label: t("kpis.rejected"), value: counters?.rejected ?? "—", Icon: BlockIcon, accent: AEROS.warning },
    { key: "error", label: t("kpis.error"), value: counters?.error ?? "—", Icon: ErrorOutlineIcon, accent: AEROS.error },
    { key: "failed", label: t("kpis.failed"), value: counters?.failed ?? "—", Icon: ReportProblemOutlinedIcon, accent: "#C08BFF" },
    { key: "latency", label: t("kpis.latency"), value: formatMs(counters?.avgDurationMs), hint: counters ? t("kpis.p95", { value: formatMs(counters.p95DurationMs) }) : null, Icon: TimerIcon, accent: AEROS.cyan },
  ], [counters, total]);

  const chart = useMemo(() => chartRows(summary?.byDay), [summary]);
  const origins = summary?.byOrigin || [];
  // Opções do filtro de origem: `summary.byOrigin` já vem filtrado pela
  // origem aplicada, então escolher uma faz a lista colapsar para 1 item.
  // Guardamos as opções à parte e só as atualizamos quando o filtro
  // aplicado não tem origem (lista "cheia"), preservando as demais opções
  // enquanto uma origem específica está selecionada.
  const [originOptions, setOriginOptions] = useState([]);
  useEffect(() => {
    if (summary && !applied.origin) setOriginOptions(summary.byOrigin || []);
  }, [summary, applied.origin]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/api-usage/report/calls", { params: { ...paramsOf(applied), page: 1, pageSize: 9999999 } });
      if (data.truncated) toast.warning(t("exportTruncated", { max: data.pageSize }));
      exportApiXlsx({ rows: data.rows || [], dateFrom: applied.dateFrom, dateTo: applied.dateTo });
    } catch (err) {
      toastError(err);
    } finally {
      setExporting(false);
    }
  };

  // A tela /tickets navega por uuid (rota /tickets/:uuid) e NÃO lê query
  // string; o log só guarda o id numérico, então resolvemos o uuid na hora.
  const abrirTicket = async (ticketId) => {
    try {
      const { data } = await api.get(`/tickets/${ticketId}`);
      if (data?.uuid) history.push(`/tickets/${data.uuid}`);
    } catch (err) {
      toastError(err);
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const pageItems = useMemo(() => {
    const items = new Set([1, pageCount, page - 1, page, page + 1].filter((n) => n >= 1 && n <= pageCount));
    const arr = [...items].sort((a, b) => a - b);
    return arr.flatMap((n, i) => (i > 0 && n - arr[i - 1] > 1 ? ["…", n] : [n]));
  }, [page, pageCount]);

  const Breakdown = ({ title, items, labelOf }) => (
    <div className={classes.card}>
      <div className={classes.cardTitle}>{title}</div>
      {!items.length && <div className={classes.secondary}>{t("breakdown.empty")}</div>}
      {items.slice(0, 8).map((it, i) => (
        <div key={i} className={classes.bRow}>
          <span className={classes.ell}>{labelOf(it)}</span>
          <b>{it.total}</b>
        </div>
      ))}
    </div>
  );

  return (
    <MainContainer>
      <div className={classes.pageHeader}>
        <div>
          <div className={classes.pageTitle}>{t("title")} <span>· {total} {t("calls")}</span></div>
          <div className={classes.pageSubtitle}>{periodLabel(applied.dateFrom, applied.dateTo)}{updatedAt ? ` · ${updatedAgo(updatedAt)}` : ""}</div>
        </div>
        <div className={classes.actions}>
          <Button className={aeros.gradientButton} startIcon={<GetAppIcon />} onClick={handleExport} disabled={exporting || !total}>{t("exportXlsx")}</Button>
        </div>
      </div>

      <div className={classes.pageBody}>
        <ReportsNav />

        <div className={classes.card}>
          <div className={classes.row}>
            <div className={classes.segmented}>
              {PRESETS.map((p) => (
                <button key={p.key} type="button" className={`${classes.seg} ${preset === p.key ? classes.segOn : ""}`} onClick={() => apply({ ...filters, ...presetRange(p.key) })}>{p.label}</button>
              ))}
            </div>
            <div className={classes.pill}>
              <EventIcon className={classes.pillIcon} />
              <input type="date" className={classes.dateField} value={filters.dateFrom} onChange={(e) => set({ dateFrom: e.target.value })} onBlur={() => apply()} aria-label={t("dateFrom")} />
              <span>→</span>
              <input type="date" className={classes.dateField} value={filters.dateTo} onChange={(e) => set({ dateTo: e.target.value })} onBlur={() => apply()} aria-label={t("dateTo")} />
            </div>
            {[
              [t("filters.connection"), "whatsappId", whatsapps.map((w) => ({ key: String(w.id), label: w.name })), t("filters.all")],
              [t("filters.origin"), "origin", originOptions.map((o) => ({ key: o.origin, label: originLabel(o.origin) })), t("filters.all")],
              [t("filters.kind"), "kind", KINDS, t("filters.allM")],
              [t("filters.route"), "route", ROUTES, t("filters.all")],
              [t("filters.channel"), "channel", CHANNELS, t("filters.allM")],
            ].map(([label, key, list, allLabel]) => (
              <div key={key} className={`${classes.pill} ${filters[key] ? classes.pillOn : ""}`}>
                {label}
                <Select native disableUnderline value={filters[key]} onChange={(e) => apply({ ...filters, [key]: e.target.value })} className={classes.pillSelect} inputProps={{ "aria-label": label }}>
                  <option value="">{allLabel}</option>
                  {list.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </Select>
              </div>
            ))}
            {hasActiveFilters(filters) && <button type="button" className={classes.clear} onClick={() => apply({ ...DEFAULT_FILTERS(), dateFrom: filters.dateFrom, dateTo: filters.dateTo })}>{t("clearFilters")}</button>}
          </div>
          <div className={classes.row}>
            <form className={classes.search} onSubmit={(e) => { e.preventDefault(); apply(); }}>
              <SearchIcon />
              <input value={filters.search} onChange={(e) => set({ search: e.target.value })} placeholder={t("searchPlaceholder")} aria-label={t("searchAria")} />
            </form>
            <div className={classes.statusPills}>
              {OUTCOME_PILLS.map((p) => {
                const on = p.key ? filters.outcome.length === 1 && filters.outcome[0] === p.key : !filters.outcome.length;
                const n = counters ? counters[p.field] : null;
                return (
                  <button key={p.key || "all"} type="button" className={`${classes.sPill} ${on ? classes.sPillOn : ""}`} onClick={() => apply({ ...filters, outcome: p.key ? [p.key] : [] })}>
                    {p.label} {n != null && <span style={{ opacity: on ? 0.7 : 1 }}>{n}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className={classes.kpis}>
          {kpis.map(({ key, label, value, hint, Icon, accent }) => (
            <div key={key} className={classes.kpi}>
              <div className={classes.kpiHead}><Icon style={{ color: accent }} />{label}</div>
              <div className={classes.kpiValue}>{value ?? "—"}</div>
              <div className={classes.kpiHint}>{hint || (summary ? "" : t("summaryUnavailable"))}</div>
            </div>
          ))}
        </div>

        <div className={classes.card}>
          <div className={classes.cardTitle}>{t("chart.title")}</div>
          {!chart.length ? <div className={classes.secondary}>{t("chart.empty")}</div> : (
            <div className={classes.chartBox}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chart} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={alpha(theme.palette.text.primary, 0.08)} />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: theme.palette.text.secondary }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: theme.palette.text.secondary }} axisLine={false} tickLine={false} />
                  <ChartTooltip content={<ApiChartTooltip theme={theme} />} />
                  <Legend formatter={(name) => t(`pills.${name}`)} wrapperStyle={{ fontSize: 11 }} />
                  {SERIES.map((s) => <Bar key={s.key} dataKey={s.key} stackId="a" fill={s.color} radius={s.key === "failed" ? [4, 4, 0, 0] : 0} />)}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className={classes.breakdowns}>
          <Breakdown title={t("breakdown.connection")} items={summary?.byConnection || []} labelOf={(it) => it.name || "—"} />
          <Breakdown title={t("breakdown.origin")} items={origins} labelOf={(it) => originLabel(it.origin)} />
          <Breakdown title={t("breakdown.kind")} items={summary?.byKind || []} labelOf={(it) => kindLabel(it.kind === "—" ? "" : it.kind)} />
          <div className={classes.card}>
            <div className={classes.cardTitle}>{t("breakdown.errors")}</div>
            {!(summary?.topErrors || []).length && <div className={classes.secondary}>{t("breakdown.empty")}</div>}
            {(summary?.topErrors || []).map((e, i) => (
              <Tooltip key={i} title={e.message || ""}>
                <div className={classes.bRow}><span className={`${classes.ell} ${classes.errCell}`}>{e.message}</span><b>{e.count}</b></div>
              </Tooltip>
            ))}
          </div>
        </div>

        <div className={aeros.tableCard}>
          <div className={classes.tableWrap}>
            <div>
              <div className={classes.tHead}>
                {["when", "connection", "origin", "route", "kind", "number", "status", "outcome", "latency", "error", "actions"].map((k) => <div key={k}>{t(`columns.${k}`)}</div>)}
              </div>
              {loading && !rows.length ? <div className={classes.empty}><CircularProgress size={22} /></div>
              : !rows.length ? <div className={classes.empty}>{t("empty")}</div>
              : rows.map((r) => {
                const oc = outcomeMeta(r.outcome);
                return (
                  <div key={r.id} className={classes.tRow}>
                    <div className={classes.secondary}>{whenLabel(r.createdAt)}</div>
                    <div className={classes.ell}>{r.whatsapp?.name || "—"}</div>
                    <div className={classes.ell}>{originLabel(r.origin)}</div>
                    <div className={classes.secondary}>{r.route}</div>
                    <div className={classes.secondary}>{kindLabel(r.kind)} · {channelLabel(r.channel)}</div>
                    <div className={classes.secondary}>{r.number || "—"}</div>
                    <div>{r.statusCode}</div>
                    <div><span className={`${classes.status} ${classes[oc.tone]}`}><i />{oc.label}</span></div>
                    <div className={classes.secondary}>{formatMs(r.durationMs)}</div>
                    <div>{r.errorMessage ? <Tooltip title={r.errorMessage}><div className={classes.errCell}>{r.errorMessage}</div></Tooltip> : <span className={classes.secondary}>—</span>}</div>
                    <div className={classes.rowActions}>
                      {r.ticketId && (
                        <Tooltip title={t("openTicket")}>
                          <IconButton className={classes.actBtn} aria-label={t("openTicket")} onClick={() => abrirTicket(r.ticketId)}><OpenInNewIcon /></IconButton>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className={classes.foot}>
            <div>{pageRange(page, pageSize, total)}</div>
            <div className={classes.pill} style={{ minHeight: 44 }}>
              <Select native disableUnderline value={pageSize} onChange={(e) => { const s = Number(e.target.value); setPageSize(s); load(applied, 1, s); }} className={classes.pillSelect} inputProps={{ "aria-label": t("perPageAria") }}>
                {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{t("perPage", { n })}</option>)}
              </Select>
            </div>
            <div className={classes.pages}>
              <button type="button" className={classes.pg} aria-label={t("prevPage")} disabled={page <= 1} onClick={() => load(applied, page - 1, pageSize)}>‹</button>
              {pageItems.map((n, i) => (n === "…" ? <span key={`d${i}`} className={classes.secondary}>…</span> : <button key={n} type="button" className={`${classes.pg} ${n === page ? classes.pgOn : ""}`} onClick={() => load(applied, n, pageSize)}>{n}</button>))}
              <button type="button" className={classes.pg} aria-label={t("nextPage")} disabled={page >= pageCount} onClick={() => load(applied, page + 1, pageSize)}>›</button>
            </div>
          </div>
        </div>
      </div>
    </MainContainer>
  );
};

export default ReportsApi;
